import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {resize} from '@homeaway/raf-optimized';

function DetermineIfMobile(WrappedComponent, mobileBreakpoint = 767) {
    const HAS_WINDOW = typeof window !== 'undefined';

    const wrappedComponentDisplayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    return class extends Component {
        static displayName = `DetermineIfMobile(${wrappedComponentDisplayName})`

        static propTypes = {
            /** Point at which mobile vs desktop views are switched. Anything below or equal to this number will result in mobile view. */
            mobileBreakpoint: PropTypes.number,
            /** Force value. This is useful for server-side rendering when you know if it is mobile. It helps prevent react's server/client mismatch warnings. */
            isMobile: PropTypes.bool
        }

        static defaultProps = {
            mobileBreakpoint
        }

        constructor(props) {
            super(props);

            this.state = {
                isMobile: this.isMobile(props)
            };
        }

        componentDidMount() {
            if (HAS_WINDOW) {
                this.clearResize = resize(this.resizeCallback);
            }
        }

        componentWillUnmount() {
            if (HAS_WINDOW) {
                this.clearResize();
            }
        }

        /**
         * resizeCallback()
         * Callback function implemented by @homeaway/raf-optimized resize() registration
         * @param {obj} sizeObj - contains "width" and "height" properties
         */
        resizeCallback = (sizeObj) => {
            const isMobile = this.isMobileByWindow(sizeObj.width);
            if (isMobile !== this.state.isMobile) {
                this.setState({isMobile});
            }
        }

        /**
         * isMobile()
         * Determine if component is mobile when we are not sure if we are in browser or node environment
         * @param {obj} props
         */
        isMobile(props) {
            if (HAS_WINDOW) {
                return this.isMobileByWindow(false);
            }
            return this.isMobileByProps(props);
        }

        /**
         * isMobileByProps()
         * Determine if component is mobile when are not in a browser environment
         * @param {obj} props
         */
        isMobileByProps(props) {
            return props.isMobile || false;
        }

        /**
         * isMobileByWindow()
         * Determine if component is mobile when are in a browser environment
         * @param {false|int} width - provided on optimized resize() events from @homeaway/raf-optimized
         */
        isMobileByWindow(width = false) {
            if (typeof this.props.isMobile !== 'undefined') {
                return this.props.isMobile;
            }
            if (!width) {
                width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
            }
            return width <= this.props.mobileBreakpoint;
        }

        render() {
            return (<WrappedComponent {...this.props} isMobile={this.state.isMobile} />);
        }
    };
}

export default DetermineIfMobile;
